import type { FontIconProps } from '@arvoreeducacao/bonsai-ds/dist/components/lib/icons/FontIcon'
import type { PaymentStatus } from '__gql-generated__/types'
import type { LanguageTokens } from 'i18n'
import type { ReactNode } from 'react'

export type User = {
  id?: string
  requiresPayment?: boolean
  hasPendingPaymentTransactions?: boolean
  teacher?: Teacher
  reader?: Reader
  onboardingV2Tutorial?: Tutorial
  gamificationOnboardingTutorial?: Tutorial
  adminPanelV2OnboardingTutorial?: Tutorial
  hasFreemiumBooks?: boolean
  isEnglishTrial?: boolean
  apiToken?: string
  accessToken?: string
  changedPassword?: boolean
  email?: string
  firstAccess?: boolean
  isAdmin?: boolean
  name?: string
  permissions?: string[]
  featureToggles?: FeatureToggles
  filledOnboardingJobInfo?: boolean
  suspicionScore?: number
  npsScore?: {
    createdAt?: string
    score?: number
    source?: string
  }
  phone?: string
  products?: Array<string>
  secondEmail?: string
  serverDateTime?: string
  storeRating?: {
    createdAt?: string
    rating?: number
  }
  username?: string
  jobPositions?: string[]
  jobAreas?: string[]
}

export type FeatureToggles = {
  readingRewards?: boolean
  layoutThema?: boolean
  sentirEmotionJournal?: boolean
  sentirEmotionJournalNotes?: boolean
  sentirEmotionJournalSecondaryEmotions?: boolean
  entityUsersOnlineStatus?: boolean
  paranaNotice?: boolean
  reportsV2?: boolean
  enableReadingFluencyReapplication?: boolean
  reportReadingGoalsEnabled?: boolean
  usageTime?: boolean
  useImportationAiBased?: boolean
  comprehensionQuestions?: boolean
  useNewImportationProcess?: boolean
  hideNews?: boolean
  pagarmeV5Enabled?: boolean
  showPressreaderIconOnHeader?: boolean
  newPermissionsManagement?: boolean
  unifyRolesAndPermissions?: boolean
  readingFluencySimplifiedResults?: boolean
  askTheBook?: boolean
  readingReportV2?: boolean
  autoCorrectionWithAi?: boolean
  fluencyEmptyState?: boolean
  newReadingAssessmentV2?: boolean
  doActivityV2?: boolean
  ottoAiEnabled?: boolean
  hiddenLeagueName?: boolean
  suspiciousActivityUsersSeduc?: boolean
  adminPanelV2Rollout?: boolean
  sharedReadingEnabled?: boolean
  storyTree?: boolean
  maple?: boolean
  runningRecords?: boolean
  rewind?: boolean
  onboardingAlunoV3?: boolean
  onboardingTeacher?: boolean
  activitiesV3?: boolean
  readAloudV2Leveling?: boolean
  summaryReportV2?: boolean
  superAutor?: boolean
  superautorShelf?: boolean
  superautorEducatorPql?: boolean
  newsTab?: boolean
  seedPrPoc?: boolean
  __typename?: 'FeatureToggles'
}

export type Tutorial = {
  id?: number
  interactions?: number
  name?: string
  state?: string
  userId?: number
  updatedAt?: string
}

export type Teacher = {
  id?: string
  showFlag?: boolean
  root?: Entity
  updateStatus?: UpdateStatus
  englishTrial?: {
    endDate?: string
    remainingDays?: number
    startDate?: string
  }
  permissions?: string
  isFamilyPaying?: boolean
  searchAvailableReadersToIndicate?: Array<AvailableReaders>
  entitiesSubtreeFlat?: Array<subTreeFlat>
  blockedBooks?: Array<string>
  entitiesSubtree?: Entities
  availableBooks?: Book[]
  rootSubtree?: {
    id?: number
    name?: string
  }[]
  teacherEntities?: {
    id?: number
    name?: string
  }[]
}

export type AppliedActivity = {
  appliedActivityAnswers?: AppliedActivityAnswer[]
  activity?: Activity
  endDate?: string
  entityId?: number
  id?: number
  readerId?: number
  startDate?: string
}

export type AppliedActivityAnswer = {
  answers?: Answer[]
  customEndDate?: string
  customStartDate?: string
  finalComment?: string
  finalGrade?: number
  id?: number
  status?: string
}

export type Answer = {
  answerChoiceId?: number
  answerDiscursive?: string
  examQuestionId?: number
  grade?: number
  id?: number
  teacherComments?: string
  aiGrade?: 'CORRECT' | 'PARTIALLY_CORRECT' | 'INCORRECT'
  aiComment?: string
  answerMatchingPairs?: Array<{
    id?: number
    leftId?: number
    rightId?: number
    isCorrect?: boolean
  }>
}

export type Entities = Array<{
  id: number
  entities: Entities
}>

export type subTreeFlat = {
  id?: string
  name?: string
  type?: string
  ancestry?: string
  entity_type?: string
}

export type AvailableReaders = {
  entity_name?: string
  id?: string
  name?: string
}

export type UpdateStatus = {
  updated: boolean
  emailConfirmed: boolean
}

export type OverallRanking = {
  percentage?: number
  points?: number
  tier?: string
}

export type Reader = ReaderLeagueInfo & {
  id?: string
  listEnglishLevels?: EnglishLevels[]
  daysLeft?: number
  permissions?: ReaderPermission
  root?: Entity
  entity?: Entity
  viewedBooks?: ViewedBook[]
  relayPaginatedFavorites?: BookAsNodeConnection
  relayPaginatedViewedBooks?: ViewedBookAsNodeConnection
  highlightsv2?: BookShelf[]
  testPeriods?: TestPeriod[]
  forest?: Forest
  showCsatForm?: boolean
  collections?: BookShelf[]
  collection?: BookShelf
  contentFilters?: Module[]
  mainScreenSuggestionsv3?: BookShelf[]
  accessDaysLeft?: number
  isFavoriteBook?: boolean
  viewedBookPosition?: ViewedBookPosition
  existsAtLeastOnePlantedTree?: boolean
  acervos?: string[]
  canAccessOnboarding?: boolean
  overallRanking?: OverallRanking
  bookSuggestions?: Book[]
  paymentStatus?: PaymentStatus
}

export type GetReader = {
  accessExpireDate?: string
  accessToken?: string
  email?: string
  id?: number
  name?: string
  permissions?: ReaderPermission
  teacherId?: number
  teacherPermissions?: string
  userId?: number
}

export type ViewedBookPosition = {
  found: boolean
  status: string
  fixed_current_page: number | null
  percentage: string | null
  resume_element_doc: string | null
  resume_element_id: string | null
  resume_element_locator: string | null
}

export type AudioBook = {
  title: string
  url: string
  artwork: string
  duration: number
  id: number
  active: boolean
  listenedTimestamp: number
  hasListened: boolean
}

export type AuthorInfo = {
  name: string
  school?: string
}

export type TextualGenres = {
  name: string
}

export type Tags = {
  name: string
}

export type GeneralSkills = {
  id?: number
  description?: string
}

export type PublicDataActivity = {
  degreeRange: string
  textualGenres?: Array<TextualGenres>
  tags?: Array<Tags>
  objective?: string
  generalSkills?: Array<GeneralSkills>
}

export type Activity = {
  authorInfo?: AuthorInfo
  checked?: boolean
  bookCount?: number
  id: number
  difficultyLevel?: 'ADVANCED' | 'INTERMEDIATE' | 'BEGINNER'
  estimatedCompletionTimeInSeconds?: number
  publicDataActivity?: PublicDataActivity
  title?: string
  isBncc?: boolean
  books?: Array<Book>
  moduleId?: number
  examQuestions?: Array<ExamQuestions>
  articles?: Array<Articles>
  degreeRange?: string
}

export type RelatedContents = {
  source: RelatedContentSource
  url?: string
}

export type ExamQuestions = {
  id?: number
  statement?: string
  type?: ExamQuestionType
  baseText?: BaseText
  bnccAbility?: BnccAbilityExamQuestion
  bnccAbilityExamQuestion?: BnccAbilityExamQuestion
  books?: Book[]
  commentedAnswer?: string
  examQuestionChoices?: ExamQuestionChoice[]
  examQuestionMatchingPairs?: Array<{
    id?: number
    leftId?: number
    leftStatement?: string
    rightId?: number
    rightStatement?: string
  }>
}

export type ExamQuestionChoice = {
  examQuestionId?: number
  id?: number
  isCorrect?: boolean
  statement?: string
}

export type BaseText = {
  id?: number
  statement?: string
}

export type BnccAbilityExamQuestion = {
  bnccAbility?: BnccAbility
}

export type BnccAbility = {
  code?: string
  degreeRange?: string
  description?: string
  id?: number
  practiceObject?: PracticeObject
  practiceObjectId?: string
}

export type PracticeObject = {
  bnccAbilities?: BnccAbility
  fieldPractice?: FieldPractice
  id?: number
  name?: string
}

export type FieldPractice = {
  actionField?: ActionField
  id?: number
  name?: string
  practiceObjects?: PracticeObject
}

export type ActionField = {
  fieldPractices?: FieldPractice[]
  id?: number
  name?: string
}

export type Book = {
  id?: number
  name?: string
  author?: string
  authors?: string[]
  slug?: string
  layout?: string
  isReadingTrail?: boolean
  v2ready?: boolean
  degree?: string
  bookDegree?: string[]
  manualInfoChecked?: boolean
  manual_info_checked?: boolean
  description?: string
  imageUrlIntermediaria?: string
  imageUrlThumb?: string
  imageUrlThumbLow?: string
  premium?: boolean
  freemium?: boolean
  premiumLock?: boolean
  lexileLevel?: number | null
  englishLevels?: Array<EnglishLevels>
  language?: string
  activities?: Array<Activity>
  audioBooks?: Array<AudioBook>
  hasEbook?: boolean
  hasAudioBook?: boolean
  lastInteraction?: string | null
  imprint?: string
  publisher?: Publisher
  edition?: string
  subtitle?: string
  bookCategory?: BookCategory[]
  bookThema?: BookThema[]
  relatedContents?: BookRelatedContent[]
  publicationYear?: number
  readingLevel?: number
  series?: string
  sentirActivities?: Array<Activity>
  qualeActivities?: Array<Activity>
  target?: { user?: User }
  downloadUrl?: string
  __typename?: string
}

export type BookThema = {
  thema?: Thema
}

export type Thema = {
  code?: string
  id?: number
  name?: string
  uiName?: string
}

export type Publisher = {
  id?: number
  name?: string
}

export type ViewedBook = {
  percentage?: string
  audioBookProgress?: string
  lastInteraction?: string | null
  isRead?: boolean
  isHidden?: boolean
  book: Book
  teacherName?: string
  deadlineDate?: string
  additionalActions?: ReactNode[]
}

export type EntityChildrenPayload = {
  ancestry?: string
  id?: number
  name?: string
  degree?: Degree
  userCount?: number
  childrenCount?: number
}

export type Entity = {
  id?: number
  name?: string
  requirePayment?: boolean
  degree?: Degree
  expirationDate?: string
  contractInfo?: ContractInfo
  ancestry?: string | null
  ancestryDetails?: Array<EntityAncestryDetails> | null
  children?: Array<EntityChildrenPayload>
  childrenCount?: number
  userCount?: number
  type?: EntityTypes
  readersPerPage?: Array<EntityReaderPayload>
  segment?: Segment
  customCategories?: CustomCategoriesProps[]
  root_acervo?: Acervo
  paymentInfo?: Payment
  maxSimultaneousUsers?: number
  currentSimultaneousUsers?: number
  nextReaderSpotAvailable?: number
  paymentPrice?: string
  daysLeft?: number
}

export type EntityTypes =
  | 'holding'
  | 'trademark'
  | 'network'
  | 'school'
  | 'class'

export type CustomCategoriesProps = {
  id?: number
  name?: LanguageTokens
  iconPath?: string
}

export type EntityAncestryDetails = {
  id: number
  name: string
}

export type EntityReaderPayload = {
  accessExpireDate?: string
  accessToken?: string
  email?: string
  id?: number
  name?: string
  permissions?: string
  teacherId?: number
  teacherPermissions?: string
  userId?: number
  degree?: string
  showUserSensitiveInfo?: boolean
}

export type TestPeriod = {
  createdAt?: string
  durationInDays?: number
  id?: number
  readerId?: number
  type?: string
}

export type Payment = {
  discount?: string
  discount_due_date?: string
  final_price?: string
  full_price?: string
  discounted_price?: string
}

export type AcquisitionPlans = {
  id: number
  name: string
  price: string
  products: ProductsPlan[]
  type: string
  iconUri: string
  productSum: string
  favorite: boolean
}

export type ProductsPlan = {
  id: number
  name: string
  price: string
}

export type Tree = {
  agePercentage?: number
  ageStatusText?: LanguageTokens
  description?: LanguageTokens
  growthTime?: number
  id?: number
  image?: string
  name?: LanguageTokens
  position?: number
  scientificName?: LanguageTokens
  specialItemUsed?: boolean
  species?: LanguageTokens
  stage?: number
}

export type TreeListing = {
  id?: number
  image?: string
  position?: number
}

export type Seeds = {
  id?: number
  description?: LanguageTokens
  growthTime?: string
  image?: string
  locked?: boolean
  lockedText?: string
  name?: LanguageTokens
  price?: number
  scientificName?: LanguageTokens
  species?: LanguageTokens
}

export type Forest = {
  coins?: number
  treeListings?: TreeListing[]
  trees?: Tree[]
  waterDropDetails?: WaterDropDetails
  seeds?: Seeds[]
  specialItems?: SpecialItem[]
  friendsForAdmin?: FriendForAdmin[]
  friends?: Friend[]
  friendTreeListings?: TreeListing[]
}

export type SpecialItem = {
  action?: LanguageTokens
  description?: string
  id?: number
  image?: string
  name?: LanguageTokens
  price?: number
  type?: string
}

export type FriendForAdmin = {
  id?: number
  name?: string
  type?: string
}

export type Friend = {
  name?: string
  readerId?: number
}

export type BuySpecialItemPayload = {
  status?: boolean
}

export type BuySeedPayload = {
  status?: boolean
  reason?: string
  message?: string
}

export type WaterDropDetails = {
  currentDrops?: number
  dropsPerLevel?: number
  maxDrops?: number
  minimumLevel?: number
  image?: string
  percentage?: number
  statusText?: string
  description?: string
  raining?: boolean
}

export type UserRankSummary = {
  entityId?: number
  profileId?: number
  rankPosition?: number
  rootName?: string
  totalTimeSpent?: number
  userName?: string
}

export type RankPositionSummary = {
  tier?: LeagueTier
  percentage?: number
  rankingUsers?: UserRankSummary[]
}

export type ReaderLeagueInfo = {
  getCurrentPoints?: number
  overallRanking?: RankPositionSummary
  overallRankingHistory?: RankingReaderHistory[]
}

export type RankingReaderHistory = {
  month?: number
  points?: number
  rankPosition?: number
  status?: 'ok' | 'waiting' | 'suspended'
  tier?: string
  year?: number
}

export type ContractInfo = {
  contractModules?: ContractModules[]
  type?: string
  usesContractModule?: boolean
  features?: Array<ContractFeatures>
}

export type ContractFeatures = {
  enabled?: boolean
  name?: string
}

export type ContractModules = {
  bookCollection?: string
  moduleName?: string
}

export type BookShelf = {
  acervoType?: string
  active?: boolean
  bgColor?: string
  bookShelfItems?: BookShelfItem[]
  books?: Book[]
  degreeRange?: string
  description?: string
  id?: number
  image?: string
  isHighlight?: boolean
  isShuffle?: boolean
  name?: string
  subtitle?: string
  orderPriority?: number
  thumb?: string
  type?: string
}

export type BookShelfItem = {
  book?: Book
  bookShelfId?: number
  id?: number
  mascot?: string
}

export type BookRemovalDates = {
  deactivationDate?: string | null
  slug?: string
}

export type BookCategory = {
  category?: Category
}

export type Category = {
  name?: string
  slug?: string
  iconIdentifier?: string
}

export type BookRelatedContent = {
  source: string
  url: string
}

export type Articles = {
  articles?: ArticleDetail[]
  totalEntries?: number
}

export type ArticleDetail = {
  id: number
  activities?: Activity[]
  slug?: string
  image?: string
  title?: string
  subtitle?: string
  degree?: Degree
  author?: string
  publisher?: string
  publishDate?: string
  description?: string
  categories?: Category[]
}

export type ArticleSearchDetail = {
  id: number
  image?: string
  title?: string
  subtitle?: string
  degree?: string
  author?: string
  publishDate?: string
}

export type EnglishLevels = {
  levels: string[]
  type: 'CEFR' | 'FOUNTAS_AND_PINNELL'
}

export type EnglishLevel = {
  level: string
}

export type BookAsNodeConnection = {
  edges?: BookAsNodeEdge[]
  pageInfo: PageInfo
}

export type BookAsNodeEdge = {
  cursor: string
  node: BookAsNode
}

export type BookAsNode = Book & {
  favoritesVisibility: boolean
}

export type ViewedBookAsNodeConnection = {
  edges?: ViewedBookAsNodeEdge[]
  pageInfo: PageInfo
}

export type ViewedBookAsNodeEdge = {
  cursor: string
  node?: ViewedBook
}

export type PageInfo = {
  endCursor: string
  hasNextPage: boolean
  hasPreviousPage?: boolean
  startCursor?: string
}

export type PillarOptions =
  | 'Felicidade e Bem-Estar'
  | 'Sabedoria'
  | 'Humanidade'
  | 'Liderança'
  | 'Moderação'
  | 'Cidadania'
  | 'Eu por mim mesmo'
  | 'Eu e o outro'
  | 'Eu e o mundo'
  | ''

export type Pillar = {
  name: PillarOptions
  iconUrl: string
}

// Only enums

export enum LeagueTier {
  BRONZE = 'bronze',
  SILVER = 'silver',
  GOLD = 'gold',
  PLATINUM = 'platinum',
  DIAMOND = 'diamond'
}

export enum Segment {
  CORPORATE = 'CORPORATE',
  INTERNAL = 'INTERNAL',
  LIBRARY = 'LIBRARY',
  OTHERS = 'OTHERS',
  PRIVATE = 'PRIVATE',
  PUBLIC = 'PUBLIC'
}

export enum Acervo {
  PREMIUM = 'premium',
  PUBLIC = 'public',
  ENGLISH = 'english',
  ENGLISH_ONLY = 'english_only',
  FREEMIUM = 'freemium'
}

export enum ReaderPermission {
  TRIAL_FIRST_ACCESS = 'trial_first_access',
  FREE_PASS = 'free_pass'
}

export enum Degree {
  INFANT = 'EI',
  FIRST_YEAR = '1',
  SECOND_YEAR = '2',
  THIRD_YEAR = '3',
  FOURTH_YEAR = '4',
  FIFTH_YEAR = '5',
  SIXTH_YEAR = '6',
  SEVENTH_YEAR = '7',
  EIGHTH_YEAR = '8',
  NINETH_YEAR = '9',
  COLLEGE = 'M',
  COLLEGE_1 = '1M',
  COLLEGE_2 = '2M',
  COLLEGE_3 = '3M',
  COLLEGE_EM = 'EM',
  TEACHER = 'F',
  CORPORATE = 'S',
  EJA = 'E',
  NON_SCHOOL = 'T',
  EF1 = 'EF1',
  EF2 = 'EF2'
}

export enum Module {
  LIVROS = 'LIVROS',
  LER = 'LER',
  ATUALIDADES = 'ATUALIDADES',
  // TODO: Trocar o nome de INGLES para COMUNICAR depois da mudança do módulo Ler
  INGLES = 'ENGLISH',
  SENTIR = 'SENTIR',
  CRIAR = 'CRIAR',
  ESCRITA = 'ESCRITA'
}

export enum ExamQuestionType {
  DISCURSIVE = 'DISCURSIVE',
  OBJECTIVE = 'OBJECTIVE',
  OBSERVATION = 'OBSERVATION',
  MATCHING_PAIR = 'MATCHING_PAIR'
}

export enum RelatedContentSource {
  YOUTUBE = 'YOUTUBE'
}

export enum FilterParameterName {
  BOOK_TYPE = 'book_type',
  CUSTOM_CATEGORIES = 'custom_categories',
  INDICATED_YEAR = 'indicated_year',
  WRITTEN_TYPE = 'written_type'
}

export enum JournalPrimaryEmotion {
  VERY_SAD = 'very_sad',
  SAD = 'sad',
  LITTLE_SAD = 'little_sad',
  NEUTRAL = 'neutral',
  LITTLE_HAPPY = 'little_happy',
  HAPPY = 'happy',
  VERY_HAPPY = 'very_happy'
}

export type JournalSecondaryEmotion = {
  slug: string
  name: string
}

export type SecondaryEmotionsList = {
  happy: JournalSecondaryEmotion[]
  neutral: JournalSecondaryEmotion[]
  sad: JournalSecondaryEmotion[]
}

export type EmotionEntry = {
  blockNote: string | null
  emotionColor: string
  id: number
  insertedAt: string
  note: string | null
  primaryEmotion: JournalPrimaryEmotion
  secondaryEmotions?: JournalSecondaryEmotion[]
}

export type EmotionEntriesByDate = {
  date: string
  entries: EmotionEntry[]
}

export type EmotionJournalEntriesByMonthReturn = {
  emotionJournalEntriesByMonth: EmotionEntriesByDate[]
}

export type EmotionJournalEntriesByMonthOperationVariables = {
  month: number
  year: number
  readerId?: number
}

type TeacherClassDegrees =
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | 'M'

export type TeacherClass = {
  id: number
  degree: TeacherClassDegrees
  name: string
}

export type ThemeType = Pick<FontIconProps, 'iconName'> & {
  id: number
  name: string
}

export type AssignmentType = {
  activityId: number
  title: string
  message: string | null
  teacherName?: string | null
  articles: ArticleDetail[]
  books: Book[]
  moduleId: number
  customActivityId: number
  createdAt: string
  appliedActivity: {
    id: number
    startDate: string | null
    endDate: string | null
    appliedActivityAnswer?: AppliedActivityAnswer | null
  }
}

export type ActivityDetail = {
  applied_by: string
  created_at: string
  end_date: string
  entity_name: string
  final_grade: string | null
  reader_name: string
  reader_id: number
  start_date: string
  status: string | null
}

export type GetReaderReadingGoalStatusQueryType = {
  me: {
    id: number
    reader: {
      id: number
      readingGoalStatus: {
        goal: number
        readingTime: number
      }
    }
  }
}

export type ReadingGoal = {
  goal: number
  startDate: string
  endDate?: string | null
  entityId: number
}

export type DeactivateReadingGoalMutationType = {
  deactivateReadingGoal: {
    readingGoals: ReadingGoal[]
  }
}

export type GetReadingGoalsByEntitiesQueryType = {
  readingGoals: ReadingGoal[]
}

export type GetReadingGoalReportData = {
  readingGoalsReport: {
    readingGoal: {
      goal: number
      startDate: string
    }
    totalCount: number
    users: {
      name: string
      readingTime: number
    }[]
  } | null
}

export type GetTeacherClassesData = {
  getTeacherClasses: {
    id: number
    name: string
    degree: string
  }[]
}

export type AnswerActivityStatusByEntity = {
  examquestionsId: number
  type: ExamQuestionType
  isCorrect?: boolean
}

export enum IndicationTargetType {
  USER = 'USER',
  ENTITY = 'ENTITY'
}

export enum IndicationContentType {
  BOOK = 'BOOK',
  ARTICLE = 'ARTICLE',
  VIDEO = 'VIDEO'
}

export type ArvoreContentType = 'book' | 'article' | 'video'

export type NewspaperStandFilter = {
  categories: { displayName: string; id: number }[]
  displayName: string
  displayNameLang: string
  id: number
  isCountry: boolean
  iso: string | null
  name: string
  publicationsCount: number
  slug: string
  titlePublicationCid: string | null
  numberOfCategories?: number
}

export type NewspaperStandPublication = {
  alternateNames: string[]
  availability: null | string
  categories: number[]
  cid: string
  countries: string[]
  countrySlug: string
  displayName: string
  displayNameLang: string
  fullDescription: string
  fullDescriptionLang: string
  id: number
  imageBackGroundColor: null | string
  isFree: boolean
  isSmart: boolean
  isSupplement: boolean
  isVirtual: boolean
  issn: string
  language: string
  latestIssue: {
    activationTime: string
    cid: string
    expungeVersion: number
    firstPage: {
      height: number
      width: number
    }
    id: number
    issueDate: string
    issueVer: number
    key: string
    layoutVersion: number
    version: number
  }
  mastheads: {
    colorImageId: string
    height: number
    whiteImageId: string
    width: number
  }
  maxBackIssues: null | number
  media: string
  name: string
  original: null | string
  prn: string
  publicationCountry: string
  publisher: {
    id: number
    name: string
  }
  rank: number
  schedule: string
  shortDescription: string
  shortDescriptionLang: string
  slug: string
  type: string
}
